import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Plus } from 'lucide-react';
import { getEmailBccSettings, updateEmailBccSettings } from '@/lib/api';
import { toast } from 'sonner';

const isValidEmail = (value) => {
  const email = String(value || '').trim();
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const dedupeEmails = (emails = []) => {
  const seen = new Set();
  const result = [];
  emails.forEach((email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
};

const EmailChipInput = ({ label, storeKey, value, onChange, testIdPrefix }) => {
  const [inputValue, setInputValue] = useState('');

  const addEmail = () => {
    const candidate = String(inputValue || '').trim().toLowerCase();
    if (!candidate) return;
    if (!isValidEmail(candidate)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const next = dedupeEmails([...(value || []), candidate]);
    onChange(next);
    setInputValue('');
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-${storeKey}`}>
      <Label data-testid={`${testIdPrefix}-label-${storeKey}`}>{label}</Label>
      <div className="flex flex-wrap gap-2 min-h-9" data-testid={`${testIdPrefix}-chips-${storeKey}`}>
        {(value || []).length === 0 ? (
          <span className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-empty-${storeKey}`}>
            No BCC emails configured.
          </span>
        ) : (
          (value || []).map((email) => (
            <Badge
              key={`${storeKey}-${email}`}
              variant="outline"
              className="inline-flex items-center gap-1"
              data-testid={`${testIdPrefix}-chip-${storeKey}-${email}`}
            >
              {email}
              <button
                type="button"
                onClick={() => onChange((value || []).filter((item) => item !== email))}
                className="rounded-full hover:bg-slate-200"
                data-testid={`${testIdPrefix}-remove-${storeKey}-${email}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addEmail();
            }
          }}
          placeholder="add@email.com"
          data-testid={`${testIdPrefix}-input-${storeKey}`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={addEmail}
          data-testid={`${testIdPrefix}-add-${storeKey}`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default function EmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [savingByStore, setSavingByStore] = useState({});
  const [settingsByStore, setSettingsByStore] = useState({});

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getEmailBccSettings();
      const rows = response?.data?.settings || [];
      const mapped = {};
      rows.forEach((row) => {
        mapped[row.store_key] = {
          store_key: row.store_key,
          store_name: row.store_name || row.store_key,
          registration_bcc: dedupeEmails(row.registration_bcc || []),
          invoice_bcc: dedupeEmails(row.invoice_bcc || []),
          updated_at: row.updated_at || null,
        };
      });
      setSettingsByStore(mapped);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const storeRows = useMemo(() => {
    return Object.values(settingsByStore).sort((a, b) => String(a.store_name || '').localeCompare(String(b.store_name || '')));
  }, [settingsByStore]);

  const updateStoreValue = (storeKey, field, value) => {
    setSettingsByStore((prev) => ({
      ...prev,
      [storeKey]: {
        ...prev[storeKey],
        [field]: dedupeEmails(value || []),
      },
    }));
  };

  const saveStoreSettings = async (storeKey) => {
    const store = settingsByStore[storeKey];
    if (!store) return;

    setSavingByStore((prev) => ({ ...prev, [storeKey]: true }));
    try {
      await updateEmailBccSettings(storeKey, {
        registration_bcc: dedupeEmails(store.registration_bcc || []),
        invoice_bcc: dedupeEmails(store.invoice_bcc || []),
      });
      toast.success(`Saved BCC settings for ${store.store_name}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save BCC settings');
    } finally {
      setSavingByStore((prev) => ({ ...prev, [storeKey]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl font-semibold" data-testid="email-settings-title">Email Settings</h1>
        <p className="text-sm text-muted-foreground" data-testid="email-settings-description">
          Configure per-store BCC recipients for new client registration emails and order invoice emails.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="email-settings-loading-state">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : storeRows.length === 0 ? (
        <Card data-testid="email-settings-empty-card">
          <CardContent className="py-8 text-sm text-muted-foreground">No store records found.</CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="email-settings-store-list">
          {storeRows.map((store) => (
            <Card key={store.store_key} data-testid={`email-settings-store-card-${store.store_key}`}>
              <CardHeader>
                <CardTitle data-testid={`email-settings-store-title-${store.store_key}`}>{store.store_name}</CardTitle>
                <CardDescription data-testid={`email-settings-store-key-${store.store_key}`}>
                  Store key: {store.store_key}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <EmailChipInput
                  label="Registration Email BCC"
                  storeKey={store.store_key}
                  value={store.registration_bcc}
                  onChange={(next) => updateStoreValue(store.store_key, 'registration_bcc', next)}
                  testIdPrefix="email-settings-registration"
                />

                <EmailChipInput
                  label="Invoice Email BCC"
                  storeKey={store.store_key}
                  value={store.invoice_bcc}
                  onChange={(next) => updateStoreValue(store.store_key, 'invoice_bcc', next)}
                  testIdPrefix="email-settings-invoice"
                />

                <div className="flex justify-end">
                  <Button
                    onClick={() => saveStoreSettings(store.store_key)}
                    disabled={Boolean(savingByStore[store.store_key])}
                    data-testid={`email-settings-save-${store.store_key}`}
                  >
                    {savingByStore[store.store_key] ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
